import {
  MarkNotificationsReadRequest,
  NotificationCategorySummary,
  NotificationDto,
  NotificationListResponse,
  NotificationSearchRequest,
  NotificationStatus,
  NotificationSummaryResponse,
} from '../service-trips-api/types/notification';
import { NotificationService } from '../notification/notification-service.interface';
import { MOCK_NOTIFICATIONS, MOCK_PASSENGER_ID } from './mock-notifications';

const RANDOM_FIRST_NAMES = [
  'Emma', 'Maja', 'Ella', 'Alice', 'Olivia',
  'Hugo', 'Lucas', 'Liam', 'William', 'Elias',
];

const RANDOM_LAST_NAMES = [
  'Andersson', 'Johansson', 'Karlsson', 'Nilsson', 'Eriksson',
  'Larsson', 'Olsson', 'Persson', 'Svensson', 'Gustafsson',
];

const RANDOM_AGREEMENTS = [
  'Lidingö kommun',
  'Stockholms stad',
  'Nacka kommun',
  'Solna stad',
  'Sundbyberg stad',
];

const STATUSES: NotificationStatus[] = ['NEW_PASSENGER', 'AWAITING_APPROVAL'];

let idCounter = 100;

const generateId = (): string => {
  idCounter += 1;
  return `mock-${idCounter}-${Date.now()}`;
};

const pickRandom = <T>(arr: readonly T[]): T => {
  const item = arr[Math.floor(Math.random() * arr.length)];
  if (item === undefined) {
    throw new Error('pickRandom called with empty array');
  }
  return item;
};

const generateRandomNotification = (): NotificationDto => ({
  id: generateId(),
  passengerId: MOCK_PASSENGER_ID,
  passengerName: `${pickRandom(RANDOM_FIRST_NAMES)} ${pickRandom(RANDOM_LAST_NAMES)}`,
  agreementName: pickRandom(RANDOM_AGREEMENTS),
  status: pickRandom(STATUSES),
  createdAt: new Date().toISOString(),
  readAt: null,
});

const computeCategories = (
  notifications: NotificationDto[]
): NotificationCategorySummary[] => {
  const unread = notifications.filter((n) => n.readAt === null);
  const newCount = unread.filter((n) => n.status === 'NEW_PASSENGER').length;
  const awaitingCount = unread.filter(
    (n) => n.status === 'AWAITING_APPROVAL'
  ).length;

  return [
    { status: 'NEW_PASSENGER' as const, count: newCount },
    { status: 'AWAITING_APPROVAL' as const, count: awaitingCount },
  ];
};

export class NotificationMockService implements NotificationService {
  private notifications: Map<string, NotificationDto>;
  private queueInterval: ReturnType<typeof setInterval> | null = null;
  private queueRefCount = 0;
  private listeners: Array<() => void> = [];

  constructor() {
    this.notifications = new Map(
      MOCK_NOTIFICATIONS.map((n) => [n.id, { ...n }])
    );
  }

  private notifyListeners(): void {
    this.listeners.forEach((fn) => fn());
  }

  onUpdate(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((fn) => fn !== listener);
    };
  }

  getSummary(): NotificationSummaryResponse {
    const all = Array.from(this.notifications.values());
    const categories = computeCategories(all);
    const totalUnread = categories.reduce((sum, c) => sum + c.count, 0);
    return { totalUnread, categories };
  }

  getNotifications(
    request: NotificationSearchRequest
  ): NotificationListResponse {
    let filtered = Array.from(this.notifications.values());

    if (request.status) {
      filtered = filtered.filter((n) => n.status === request.status);
    }

    if (request.searchString) {
      const search = request.searchString.toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.passengerName.toLowerCase().includes(search) ||
          String(n.passengerId).includes(search) ||
          n.agreementName.toLowerCase().includes(search)
      );
    }

    filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    const start = (request.pageNumber - 1) * request.itemsPerPage;
    const paged = filtered.slice(start, start + request.itemsPerPage);
    const categories = computeCategories(
      Array.from(this.notifications.values())
    );

    return {
      notifications: paged,
      totalCount: filtered.length,
      pageNumber: request.pageNumber,
      itemsPerPage: request.itemsPerPage,
      categories,
    };
  }

  markAsRead(request: MarkNotificationsReadRequest): void {
    const now = new Date().toISOString();
    for (const id of request.notificationIds) {
      const notification = this.notifications.get(id);
      if (notification) {
        notification.readAt = now;
      }
    }
    this.notifyListeners();
  }

  markAllAsRead(): void {
    const now = new Date().toISOString();
    for (const notification of this.notifications.values()) {
      if (notification.readAt === null) {
        notification.readAt = now;
      }
    }
    this.notifyListeners();
  }

  startPush(minIntervalMs = 8000, maxIntervalMs = 15000): void {
    this.queueRefCount += 1;
    if (this.queueInterval) return;

    const scheduleNext = (): void => {
      const delay =
        minIntervalMs + Math.random() * (maxIntervalMs - minIntervalMs);
      this.queueInterval = setTimeout(() => {
        const notification = generateRandomNotification();
        this.notifications.set(notification.id, notification);
        this.notifyListeners();
        scheduleNext();
      }, delay);
    };

    scheduleNext();
  }

  stopPush(): void {
    this.queueRefCount = Math.max(0, this.queueRefCount - 1);
    if (this.queueRefCount === 0 && this.queueInterval) {
      clearTimeout(this.queueInterval);
      this.queueInterval = null;
    }
  }

  reset(): void {
    this.queueRefCount = 0;
    if (this.queueInterval) {
      clearTimeout(this.queueInterval);
      this.queueInterval = null;
    }
    this.listeners = [];
    this.notifications = new Map(
      MOCK_NOTIFICATIONS.map((n) => [n.id, { ...n }])
    );
  }
}

export const notificationMockService = new NotificationMockService();
